const SITE_NAME = 'Bordergarita';
const SITE_URL = 'https://bordergarita.com';
const LOGO_URL = '';

const TAG_TYPES = {
  meta: { label: '<Meta/>', code: 'meta', self_closing: true },
  link: { label: '<Link/>', code: 'link', self_closing: true },
  title: { label: '<Title></Title>', code: 'title', self_closing: false },
  script: { label: '<Script></Script>', code: 'script', self_closing: false },
};

/** Stable pack options shown in the Generate SEO pack modal (blogs). */
export const BLOG_SEO_PACK_OPTIONS = [
  {
    id: 'seo_principal',
    label: 'SEO principal',
    description: 'title, description, keywords, author, canonical',
    defaultChecked: true,
  },
  {
    id: 'open_graph',
    label: 'Open Graph',
    description: 'og:title, description, type, url, site_name, image',
    defaultChecked: true,
  },
  {
    id: 'twitter',
    label: 'Twitter / X Cards',
    description: 'twitter:card, title, description, image',
    defaultChecked: true,
  },
  {
    id: 'bots_hreflang',
    label: 'Bots and languages',
    description: 'robots, Content-Language, hreflang',
    defaultChecked: true,
  },
  {
    id: 'geo',
    label: 'Geolocalización',
    description: 'geo.region, placename, position, ICBM (vacío)',
    defaultChecked: false,
  },
  {
    id: 'article',
    label: 'Schema · Article',
    description: 'JSON-LD Article',
    defaultChecked: true,
  },
  {
    id: 'news_article',
    label: 'Schema · NewsArticle',
    description: 'JSON-LD NewsArticle',
    defaultChecked: false,
  },
  {
    id: 'faq',
    label: 'Schema · FAQPage',
    description: 'From FAQs section',
    defaultChecked: true,
  },
  {
    id: 'breadcrumb',
    label: 'Schema · BreadcrumbList',
    description: 'Home › Blog › page',
    defaultChecked: true,
  },
  {
    id: 'product',
    label: 'Schema · Product + AggregateRating',
    description: 'Optional — empty ratings',
    defaultChecked: false,
  },
];

function metaTag(seoName, attrs) {
  return {
    name: seoName,
    type: TAG_TYPES.meta,
    content: '',
    attributes: Object.entries(attrs).map(([key, value]) => ({
      key,
      value: value == null ? '' : String(value),
    })),
  };
}

function linkTag(seoName, attrs) {
  return {
    name: seoName,
    type: TAG_TYPES.link,
    content: '',
    attributes: Object.entries(attrs).map(([key, value]) => ({
      key,
      value: value == null ? '' : String(value),
    })),
  };
}

function titleTag(seoName, text) {
  return {
    name: seoName,
    type: TAG_TYPES.title,
    content: text || '',
    attributes: [],
  };
}

function scriptLdJson(seoName, data) {
  return {
    name: seoName,
    type: TAG_TYPES.script,
    content: JSON.stringify(data, null, 2),
    attributes: [{ key: 'type', value: 'application/ld+json' }],
  };
}

function absUrl(baseUrl, path) {
  const base = (baseUrl || SITE_URL).replace(/\/$/, '');
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

function langCode(lang) {
  if (!lang) return '';
  if (typeof lang === 'string') return lang.toLowerCase();
  return (lang.code || '').toLowerCase();
}

function plainTextFromHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function faqAnswerText(faq) {
  return (
    (faq?.answer || '').trim() ||
    plainTextFromHtml(faq?.answer_html_updated || faq?.answer_html || '')
  );
}

function buildArticleSchemaLd(type, ctx, { title, description, image, pageUrl, siteUrl }) {
  return {
    '@context': 'https://schema.org',
    '@type': type,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    headline: title,
    description,
    image: image,
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
    datePublished: ctx.datePublished || '',
    dateModified: ctx.dateModified || '',
  };
}

/**
 * Build editor-format meta tags for selected packs.
 * Values from the blog are filled; remaining dynamic placeholders stay "".
 *
 * @param {object} ctx
 * @param {string[]} selectedPackIds
 */
export function buildBlogSeoPackTags(ctx, selectedPackIds) {
  const selected = new Set(selectedPackIds || []);
  const title = (ctx.title || '').trim();
  const description = (ctx.description || '').trim();
  const image = absUrl(ctx.siteUrl, (ctx.image || '').trim());
  const lang = langCode(ctx.lang);
  const pageUrl = absUrl(ctx.siteUrl, ctx.full_slug);
  const siteUrl = (ctx.siteUrl || SITE_URL).replace(/\/$/, '');
  const homeUrl = lang ? `${siteUrl}/${lang}` : siteUrl;
  const blogListUrl = lang ? `${siteUrl}/${lang}/blog` : `${siteUrl}/blog`;
  const articleCtx = { title, description, image, pageUrl, siteUrl };
  const tags = [];

  if (selected.has('seo_principal')) {
    tags.push(titleTag('seo:title', title));
    tags.push(metaTag('seo:description', { name: 'description', content: description }));
    tags.push(metaTag('seo:keywords', { name: 'keywords', content: '' }));
    tags.push(metaTag('seo:author', { name: 'author', content: SITE_NAME }));
    tags.push(metaTag('seo:meta-title', { name: 'title', content: title }));
    tags.push(linkTag('seo:canonical', { rel: 'canonical', href: pageUrl }));
  }

  if (selected.has('open_graph')) {
    tags.push(metaTag('seo:og:title', { property: 'og:title', content: title }));
    tags.push(metaTag('seo:og:description', { property: 'og:description', content: description }));
    tags.push(metaTag('seo:og:type', { property: 'og:type', content: 'article' }));
    tags.push(metaTag('seo:og:url', { property: 'og:url', content: pageUrl }));
    tags.push(metaTag('seo:og:site_name', { property: 'og:site_name', content: SITE_NAME }));
    tags.push(metaTag('seo:og:image', { property: 'og:image', content: image }));
  }

  if (selected.has('twitter')) {
    tags.push(metaTag('seo:twitter:card', { name: 'twitter:card', content: 'summary_large_image' }));
    tags.push(metaTag('seo:twitter:title', { name: 'twitter:title', content: title }));
    tags.push(
      metaTag('seo:twitter:description', { name: 'twitter:description', content: description })
    );
    tags.push(metaTag('seo:twitter:image', { name: 'twitter:image', content: image }));
  }

  if (selected.has('bots_hreflang')) {
    tags.push(metaTag('seo:robots', { name: 'robots', content: 'index, follow' }));
    tags.push(
      metaTag('seo:content-language', {
        'http-equiv': 'Content-Language',
        content: lang || '',
      })
    );
    if (lang && pageUrl) {
      tags.push(
        linkTag(`seo:hreflang:${lang}`, {
          rel: 'alternate',
          hreflang: lang,
          href: pageUrl,
        })
      );
    }
    const translations = Array.isArray(ctx.translations) ? ctx.translations : [];
    translations.forEach((t) => {
      const altLang = langCode(t.lang);
      if (!altLang || altLang === lang) return;
      const altPath = t.full_slug || '';
      const altUrl = altPath ? absUrl(ctx.siteUrl, altPath) : '';
      tags.push(
        linkTag(`seo:hreflang:${altLang}`, {
          rel: 'alternate',
          hreflang: altLang,
          href: altUrl,
        })
      );
    });
    tags.push(
      linkTag('seo:hreflang:x-default', {
        rel: 'alternate',
        hreflang: 'x-default',
        href: pageUrl,
      })
    );
  }

  if (selected.has('geo')) {
    tags.push(metaTag('seo:geo.region', { name: 'geo.region', content: '' }));
    tags.push(metaTag('seo:geo.placename', { name: 'geo.placename', content: '' }));
    tags.push(metaTag('seo:geo.position', { name: 'geo.position', content: '' }));
    tags.push(metaTag('seo:ICBM', { name: 'ICBM', content: '' }));
  }

  if (selected.has('article')) {
    tags.push(scriptLdJson('seo:schema:article', buildArticleSchemaLd('Article', ctx, articleCtx)));
  }

  if (selected.has('news_article')) {
    tags.push(
      scriptLdJson('seo:schema:newsarticle', buildArticleSchemaLd('NewsArticle', ctx, articleCtx))
    );
  }

  if (selected.has('faq')) {
    const items = (ctx.faqs || [])
      .map((faq) => ({
        '@type': 'Question',
        name: (faq?.question || '').trim(),
        acceptedAnswer: {
          '@type': 'Answer',
          text: faqAnswerText(faq),
        },
      }))
      .filter((q) => q.name);

    tags.push(
      scriptLdJson('seo:schema:faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.length
          ? items
          : [
              {
                '@type': 'Question',
                name: '',
                acceptedAnswer: { '@type': 'Answer', text: '' },
              },
            ],
      })
    );
  }

  if (selected.has('breadcrumb')) {
    const homeName = lang === 'es' ? 'Inicio' : 'Home';
    const blogName = 'Blog';
    tags.push(
      scriptLdJson('seo:schema:breadcrumb', {
        '@context': 'https://schema.org/',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: homeName,
            item: homeUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: blogName,
            item: blogListUrl,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: title,
            item: pageUrl,
          },
        ],
      })
    );
  }

  if (selected.has('product')) {
    tags.push(
      scriptLdJson('seo:schema:product', {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: title,
        image: image,
        description,
        brand: {
          '@type': 'Brand',
          name: SITE_NAME,
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '',
          bestRating: '',
          worstRating: '',
          ratingCount: '',
          reviewCount: '',
        },
      })
    );
  }

  return tags;
}

function isBlankPlaceholderTag(tag) {
  if (!tag) return true;
  if (tag.name || tag.type?.code || tag.type) return false;
  const attrs = tag.attributes || [];
  if (!attrs.length) return true;
  return attrs.every((a) => !a.key && !a.value);
}

/**
 * Merge generated pack tags into existing list.
 * Never overwrites a tag that already has the same `name` (seo:*).
 */
export function mergeBlogSeoPackTags(existing = [], incoming = []) {
  const current = Array.isArray(existing) ? [...existing] : [];
  const existingNames = new Set(
    current.map((t) => t?.name).filter((n) => typeof n === 'string' && n.length > 0)
  );

  const toAdd = [];
  let skipped = 0;
  for (const tag of incoming) {
    if (tag?.name && existingNames.has(tag.name)) {
      skipped += 1;
      continue;
    }
    toAdd.push(tag);
    if (tag?.name) existingNames.add(tag.name);
  }

  let base = current;
  if (base.length === 1 && isBlankPlaceholderTag(base[0])) {
    base = [];
  }

  return {
    tags: [...base, ...toAdd],
    added: toAdd.length,
    skipped,
  };
}

export function defaultSelectedPackIds(hasFaqs = false) {
  return BLOG_SEO_PACK_OPTIONS.filter((o) => {
    if (o.id === 'faq') return hasFaqs;
    return o.defaultChecked;
  }).map((o) => o.id);
}
